import { io, Socket } from 'socket.io-client';
import { store } from '@/storeVuex';

const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || 'https://stagingapi.builtsimple.com.au';

interface SocketEvents {
  notification: (data: NotificationPayload) => void;
  notification_read: (data: { id: number }) => void;
  notification_deleted: (data: { id: number }) => void;
  notifications_all_read: (data: Record<string, never>) => void;
  user_connected: (data: { user_id: string }) => void;
  user_disconnected: (data: { user_id: string }) => void;
  schedule_updated: (data: ScheduleUpdatePayload) => void;
  force_logout: (data: { reason?: string }) => void;
}

export interface ScheduleUpdatePayload {
  job_id: number;
  schedule_id: number;
  action: string;
  job_name: string;
  updated_by: number;
}

export interface NotificationPayload {
  id: number;
  title: string;
  message: string;
  type?: string;
  data?: any;
  created_at: string;
  is_read: boolean;
}

class SocketService {
  private socket: Socket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private listeners: Map<string, Set<Function>> = new Map();
  private isConnecting = false;

  get isConnected(): boolean {
    return this.socket?.connected || false;
  }

  get socketId(): string | null {
    return this.socket?.id || null;
  }

  connect(token?: string): Promise<void> {
    return new Promise((resolve, reject) => {
      // Connects to VITE_SOCKET_URL (bs-socket-server). Transport/connect
      // errors are handled gracefully below (resolve, don't reject) so the
      // app still works without realtime when the socket host is unreachable.
      if (this.socket?.connected) {
        console.log('🔌 Socket already connected');
        resolve();
        return;
      }

      if (this.isConnecting) {
        console.log('🔌 Socket connection in progress...');
        resolve();
        return;
      }

      this.isConnecting = true;

      // Get token from store if not provided
      const authToken = token || store?.state?.user?.token;

      if (!authToken) {
        console.warn('⚠️ No auth token available for socket connection');
        this.isConnecting = false;
        reject(new Error('No auth token available'));
        return;
      }

      console.log('🔌 Connecting to Socket.IO server:', SOCKET_URL);

      const userId = store?.state?.user?.user?.id;

      this.socket = io(SOCKET_URL, {
        auth: {
          token: authToken,
          user_id: userId,
        },
        transports: ['polling', 'websocket'],  // Try polling first for better reliability
        reconnection: true,
        reconnectionAttempts: this.maxReconnectAttempts,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        timeout: 20000,
        forceNew: true  // Force new connection
      });

      this.socket.on('connect', () => {
        console.log('✅ Socket.IO connected successfully');
        console.log('🔗 Socket ID:', this.socket?.id);
        this.reconnectAttempts = 0;
        this.isConnecting = false;

        // Emit user connected event
        const userId = store?.state?.user?.user?.id;
        if (userId) {
          this.socket?.emit('user_online', { user_id: userId });
        }

        resolve();
      });

      this.socket.on('connect_error', () => {
        // Socket.IO transport errors (xhr poll error, websocket error, etc.) are
        // handled by socket.io's built-in reconnection — don't log or reject,
        // otherwise Sentry's console + unhandled-rejection capture creates noisy issues.
        this.reconnectAttempts++;
        this.isConnecting = false;
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
          // Resolve instead of rejecting; the app continues without realtime,
          // and the next successful auth will trigger a fresh connect attempt.
          resolve();
        }
      });

      this.socket.on('disconnect', (reason) => {
        this.isConnecting = false;
        if (reason === 'io server disconnect') {
          this.socket?.connect();
        }
      });

      this.socket.on('reconnect', () => {
        this.reconnectAttempts = 0;
      });

      this.socket.on('reconnect_error', (error) => {
        // console.log (not error) so Sentry's captureConsoleIntegration
        // doesn't ship transient WS reconnect noise to Sentry — the
        // socket retries on its own and the user sees the reconnect.
        console.log('❌ Socket.IO reconnection error:', error.message);
      });

      this.socket.on('error', (error) => {
        // Same reasoning as the reconnect_error handler above.
        console.log('❌ Socket.IO error:', error);
      });

      // Set up default notification listeners
      this.setupNotificationListeners();
    });
  }

  private setupNotificationListeners(): void {
    if (!this.socket) return;

    // Listen for new notifications
    this.socket.on('notification', (data: any) => {
      console.log('🔔 New notification received:', data);
      // Check if this is a schedule_updated event routed through notification
      if (data.type === 'schedule_updated' && data.data) {
        console.log('📅 Schedule update via notification channel:', data.data);
        this.emit('schedule_updated', data.data);
        return;
      }
      this.emit('notification', data);
    });

    // Listen for notification read events
    this.socket.on('notification_read', (data: { id: number }) => {
      console.log('📖 Notification marked as read:', data.id);
      this.emit('notification_read', data);
    });

    // Listen for notification deleted events
    this.socket.on('notification_deleted', (data: { id: number }) => {
      console.log('🗑️ Notification deleted:', data.id);
      this.emit('notification_deleted', data);
    });

    // Listen for bulk notifications read
    this.socket.on('notifications_all_read', () => {
      console.log('📖 All notifications marked as read');
      this.emit('notifications_all_read', {});
    });

    // Listen for schedule update events
    this.socket.on('schedule_updated', (data: ScheduleUpdatePayload) => {
      console.log('📅 Schedule updated:', data);
      this.emit('schedule_updated', data);
    });

    // Listen for force logout from admin
    this.socket.on('force_logout', async (data: { reason?: string }) => {
      console.log('🚪 Force logout received from admin:', data?.reason || 'No reason provided');

      // Show modal FIRST, then logout happens when user clicks "Sign In"
      this.showForceLogoutModal(data?.reason, async () => {
        try {
          const { useAuthStore } = await import('@/stores/auth');
          const authStore = useAuthStore();
          await authStore.logout();
        } catch (error) {
          // Fallback: clear storage and redirect manually
          localStorage.clear();
          sessionStorage.clear();
          window.location.href = '/';
        }
      });
    });
  }

  disconnect(): void {
    if (this.socket) {
      console.log('🔌 Disconnecting Socket.IO...');

      // Emit user offline event
      const userId = store?.state?.user?.user?.id;
      if (userId) {
        this.socket.emit('user_offline', { user_id: userId });
      }

      this.socket.disconnect();
      this.socket = null;
      this.listeners.clear();
      this.isConnecting = false;
      console.log('✅ Socket.IO disconnected');
    }
  }

  // Subscribe to socket events
  on<K extends keyof SocketEvents>(event: K, callback: SocketEvents[K]): void;
  on(event: string, callback: Function): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)?.add(callback);
  }

  // Unsubscribe from socket events
  off(event: string, callback?: Function): void {
    if (callback) {
      this.listeners.get(event)?.delete(callback);
    } else {
      this.listeners.delete(event);
    }
  }

  // Emit events to listeners
  private emit(event: string, data: any): void {
    this.listeners.get(event)?.forEach((callback) => {
      try {
        callback(data);
      } catch (error) {
        console.error(`Error in socket event listener for ${event}:`, error);
      }
    });
  }

  // Send events to server
  send(event: string, data?: any): void {
    if (this.socket?.connected) {
      this.socket.emit(event, data);
      return;
    }
    // Socket is intentionally disabled outside production (see connect()),
    // so don't log in that case — the no-op is expected.
    if (import.meta.env.MODE !== 'production') {
      return;
    }
    // Use console.log (not warn) so Sentry's captureConsoleIntegration
    // doesn't ship this to issues. This fires during login when the WS
    // handshake hasn't completed yet — the authenticate event reaches
    // here before .connected flips true. Reconnection logic in connect()
    // handles the retry; this is operationally normal, not actionable.
    console.log('Socket not connected yet, skipping event:', event);
  }

  // Join a room (for targeted notifications)
  joinRoom(room: string): void {
    this.send('join_room', { room });
  }

  // Leave a room
  leaveRoom(room: string): void {
    this.send('leave_room', { room });
  }

  // Authenticate user after login
  authenticate(userId: string | number, token: string): void {
    this.send('authenticate', { user_id: userId, token });
  }

  // Show a styled force logout modal
  private showForceLogoutModal(reason?: string, onDismiss?: () => void): void {
    const overlay = document.createElement('div');
    overlay.style.cssText = `
      position: fixed; top: 0; left: 0; width: 100%; height: 100%;
      background: rgba(0, 0, 0, 0.6); backdrop-filter: blur(4px);
      display: flex; align-items: center; justify-content: center;
      z-index: 99999; animation: fadeIn 0.3s ease;
    `;

    const modal = document.createElement('div');
    modal.style.cssText = `
      background: #fff; border-radius: 16px; padding: 40px 36px 32px;
      max-width: 420px; width: 90%; text-align: center;
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
      animation: slideUp 0.3s ease;
    `;

    modal.innerHTML = `
      <div style="
        width: 64px; height: 64px; margin: 0 auto 20px;
        background: #FEE2E2; border-radius: 50%;
        display: flex; align-items: center; justify-content: center;
      ">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#DC2626" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
          <polyline points="16 17 21 12 16 7"/>
          <line x1="21" y1="12" x2="9" y2="12"/>
        </svg>
      </div>
      <h2 style="margin: 0 0 8px; font-size: 22px; font-weight: 700; color: #1a1a1a;">
        Session Ended
      </h2>
      <p style="margin: 0 0 28px; font-size: 15px; color: #6b7280; line-height: 1.5;">
        Your session has been ended. Please sign in to continue.
      </p>
      <button id="force-logout-btn" style="
        background: #4F46E5; color: #fff; border: none; border-radius: 10px;
        padding: 12px 40px; font-size: 15px; font-weight: 600;
        cursor: pointer; transition: background 0.2s;
      ">
        Sign In
      </button>
    `;

    // Add animations
    const style = document.createElement('style');
    style.textContent = `
      @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
      @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
      #force-logout-btn:hover { background: #4338CA !important; }
    `;
    document.head.appendChild(style);

    overlay.appendChild(modal);
    document.body.appendChild(overlay);

    const btn = document.getElementById('force-logout-btn');
    if (btn) {
      btn.addEventListener('click', async () => {
        if (onDismiss) {
          await onDismiss();
        }
        window.location.href = '/';
      });
    }
  }
}

// Export singleton instance
export const socketService = new SocketService();

// Export class for testing
export { SocketService };
